import { useCallback, useState } from 'react';
import { ButtonBase, Menu, Typography } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import ExpandMoreIcon from 'src/assets/icons/ExpandMoreIcon';
import { ButtonGenerationMode } from 'src/components/ButtonGenerationMode';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { TruncatedTypography } from 'src/components/TruncatedTypography';
import { doesToolSupportCampaignKeywords } from 'src/data/generationTool';

import { useStyles } from './useStyles';

const MENU_PROPS = {
  anchorOrigin: {
    vertical: 'top',
    horizontal: 'center',
  },
  transformOrigin: {
    vertical: 'bottom',
    horizontal: 'center',
  },
  getContentAnchorEl: null,
};

export const GenerationModeList = ({
  className,
  modes,
  selectedId,
  onSelect,
  generationTool,
  disabled,
  isRecomposeTool,
  brandVoicesAssets,
  isCustomFormulasVisible,
  error,
  onAddCustomFormula,
  hideCreateNew,
}) => {
  const styles = useStyles();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSelect = useCallback(
    (mode) => {
      setAnchorEl(null);

      onSelect(mode);
    },
    [onSelect]
  );

  const handleClose = () => {
    setAnchorEl(null);
  };
  const flattenFormulas = brandVoicesAssets.reduce(
    (acc, { customFormulas }) => [...acc, ...customFormulas],
    []
  );
  const selectedMode = [...modes, ...flattenFormulas].find((m) => m.id === selectedId);

  const hasMultipleModes = modes.length > 1 || isCustomFormulasVisible;

  return (
    <>
      <ButtonBase
        className={cx(styles.root, className, {
          [styles.open]: open,
          [styles.unclickable]: !hasMultipleModes,
          [styles.error]: error,
        })}
        classes={{
          endIcon: styles.endIcon,
        }}
        onClick={hasMultipleModes ? handleClick : null}
        disabled={disabled}
        variant="text"
        color="default"
        data-testid="GenerationModeList__Button"
      >
        <div className={styles.textWrapper}>
          <CustomTypography
            className={styles.title}
            variant={TYPOGRAPHY_VARIANT.regular14}
          >
            {selectedMode?.title}
          </CustomTypography>

          {selectedMode?.description && (
            <TruncatedTypography
              className={styles.description}
              variant={TYPOGRAPHY_VARIANT.regular14}
              title={selectedMode?.description}
            >
              ({selectedMode?.description})
            </TruncatedTypography>
          )}
        </div>
        {hasMultipleModes && <ExpandMoreIcon className={styles.iconDropDown} />}
      </ButtonBase>

      <Menu
        classes={{
          paper: cx(styles.menu_paper, {
            [styles.wider]: isRecomposeTool || doesToolSupportCampaignKeywords(generationTool),
          }),
          list: styles.menu_list,
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        variant="menu"
        keepMounted
        {...MENU_PROPS}
      >
        {isCustomFormulasVisible && (
          <div className={styles.customFormulas}>
            {brandVoicesAssets.map(
              ({ id, name, customFormulas }) =>
                customFormulas.length > 0 && (
                  <div
                    className={styles.customFormulasGroupWrapper}
                    key={id}
                  >
                    <TruncatedTypography className={styles.listTitle}>{name}</TruncatedTypography>
                    {customFormulas.map((formula) => (
                      <ButtonGenerationMode
                        className={styles.button}
                        classes={{
                          title: styles.customFormulaTitle,
                          description: styles.customFormulaDescription,
                        }}
                        key={formula.id}
                        title={formula.title}
                        description={formula.description}
                        onClick={() => handleSelect(formula)}
                        selected={formula.id === selectedId}
                        disabled={disabled}
                      />
                    ))}
                  </div>
                )
            )}
          </div>
        )}

        <Typography className={styles.listTitle}>Preset formulas</Typography>
        {modes.map((mode) => (
          <ButtonGenerationMode
            className={styles.button}
            key={mode.id}
            title={mode.title}
            description={mode.description}
            onClick={() => handleSelect(mode)}
            selected={mode.id === selectedId}
            disabled={disabled}
          />
        ))}

        {!hideCreateNew && (
          <ButtonGenerationMode
            onClick={() => onAddCustomFormula(handleClose)}
            title="+ New"
            className={styles.button}
            classes={{ title: styles.newButtonTitle }}
          />
        )}
      </Menu>
    </>
  );
};

GenerationModeList.propTypes = {
  className: PropTypes.string,
  modes: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelect: PropTypes.func.isRequired,
  generationTool: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  isRecomposeTool: PropTypes.bool,
  brandVoicesAssets: PropTypes.arrayOf(PropTypes.shape()),
  isCustomFormulasVisible: PropTypes.bool,
  error: PropTypes.bool,
  onAddCustomFormula: PropTypes.func.isRequired,
  hideCreateFormula: PropTypes.bool,
  hideCreateNew: PropTypes.bool,
};

GenerationModeList.defaultProps = {
  className: null,
  selectedId: '',
  generationTool: null,
  disabled: false,
  loading: false,
  isRecomposeTool: false,
  brandVoicesAssets: [],
  isCustomFormulasVisible: false,
  error: false,
  hideCreateFormula: false,
  hideCreateNew: false,
};
